// ABOUTME: Fullscreen image lightbox with gallery navigation
// ABOUTME: Supports keyboard (arrows, escape), pinch-to-zoom, and swipe gestures

"use client"

import { useEffect, useCallback, useState, useMemo, useRef, lazy, Suspense } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight, faXmark, faMagnifyingGlassPlus, faMagnifyingGlassMinus, faCircleInfo, faCamera, faClock, faLocationDot, faUser } from "@fortawesome/free-solid-svg-icons"
import type { ExifData, LightboxImage } from "./lightbox-provider"

// Lazy load the zoom components to avoid loading the library on every page
const TransformWrapper = lazy(() => import("react-zoom-pan-pinch").then(mod => ({ default: mod.TransformWrapper })))
const TransformComponent = lazy(() => import("react-zoom-pan-pinch").then(mod => ({ default: mod.TransformComponent })))

// Import types for the ref
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch"

function hasExifData(exif?: ExifData): boolean {
  if (!exif) return false
  return !!(exif.camera || exif.lens || exif.focalLength || exif.aperture ||
            exif.shutter || exif.iso || exif.date || exif.location ||
            exif.artist || exif.copyright)
}

function ExifPanel({ exif }: { exif: ExifData }) {
  const hasCameraInfo = !!(exif.camera || exif.lens)
  const hasSettings = !!(exif.focalLength || exif.aperture || exif.shutter || exif.iso)
  const hasDateTime = !!exif.date
  const hasLocation = !!exif.location
  const hasCreator = !!(exif.artist || exif.copyright)

  return (
    <div className="bg-black/80 backdrop-blur-sm rounded-lg p-3 text-white text-sm min-w-[200px] max-w-[280px]">
      {/* Camera & Lens */}
      {hasCameraInfo && (
        <div className="flex items-start gap-2 mb-2">
          <FontAwesomeIcon icon={faCamera} className="w-4 h-4 mt-0.5 text-white/60 shrink-0" />
          <div>
            {exif.camera && <div className="font-medium">{exif.camera}</div>}
            {exif.lens && <div className="text-white/70 text-xs">{exif.lens}</div>}
          </div>
        </div>
      )}

      {/* Settings */}
      {hasSettings && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-white/80 mb-2">
          {exif.focalLength && <span>{exif.focalLength}</span>}
          {exif.aperture && <span>{exif.aperture}</span>}
          {exif.shutter && <span>{exif.shutter}</span>}
          {exif.iso && <span>ISO {exif.iso}</span>}
        </div>
      )}

      {/* Date */}
      {hasDateTime && (
        <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
          <FontAwesomeIcon icon={faClock} className="w-3 h-3 text-white/50" />
          <span>{exif.date}</span>
        </div>
      )}

      {/* Location */}
      {hasLocation && (
        <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
          <FontAwesomeIcon icon={faLocationDot} className="w-3 h-3 text-white/50" />
          <span>{exif.location}</span>
        </div>
      )}

      {/* Creator/Copyright */}
      {hasCreator && (
        <div className="flex items-center gap-2 text-xs text-white/70 border-t border-white/20 pt-2 mt-1">
          <FontAwesomeIcon icon={faUser} className="w-3 h-3 text-white/50" />
          <span>{exif.copyright || exif.artist}</span>
        </div>
      )}
    </div>
  )
}

interface LightboxProps {
  images: LightboxImage[]
  initialIndex: number
  isOpen: boolean
  onClose: () => void
}

export function Lightbox({ images, initialIndex, isOpen, onClose }: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null)
  const [isZoomed, setIsZoomed] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transformRef = useRef<ReactZoomPanPinchRef>(null)

  // Filter out invalid images - memoize to avoid recalculating on every render
  const validImages = useMemo(() => {
    return (images || []).filter(img => img && img.src)
  }, [images])

  const hasMultiple = validImages.length > 1

  // Smooth transition between images - quick fade
  const transitionToIndex = useCallback((newIndex: number) => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentIndex(newIndex)
      setTimeout(() => setIsTransitioning(false), 30)
    }, 80)
  }, [])

  const goNext = useCallback(() => {
    if (hasMultiple && !isZoomed && !isTransitioning) {
      const newIndex = (currentIndex + 1) % validImages.length
      transitionToIndex(newIndex)
    }
  }, [hasMultiple, validImages.length, isZoomed, isTransitioning, currentIndex, transitionToIndex])

  const goPrev = useCallback(() => {
    if (hasMultiple && !isZoomed && !isTransitioning) {
      const newIndex = (currentIndex - 1 + validImages.length) % validImages.length
      transitionToIndex(newIndex)
    }
  }, [hasMultiple, validImages.length, isZoomed, isTransitioning, currentIndex, transitionToIndex])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleZoomIn = useCallback(() => {
    transformRef.current?.zoomIn(0.5)
  }, [])

  const handleZoomOut = useCallback(() => {
    transformRef.current?.zoomOut(0.5)
  }, [])

  const handleResetZoom = useCallback(() => {
    transformRef.current?.resetTransform()
  }, [])

  // Track zoom state from transform events
  const handleTransform = useCallback((ref: ReactZoomPanPinchRef) => {
    setIsZoomed(ref.state.scale > 1.05)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isZoomed) {
          handleResetZoom()
        } else {
          handleClose()
        }
      }
      if (e.key === "ArrowRight" && !isZoomed) goNext()
      if (e.key === "ArrowLeft" && !isZoomed) goPrev()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, handleClose, goNext, goPrev, isZoomed, handleResetZoom])

  // Reset index and zoom when opening with new images/initialIndex
  useEffect(() => {
    if (isOpen) {
      const safeInitial = Math.min(Math.max(0, initialIndex), Math.max(0, validImages.length - 1))
      setCurrentIndex(safeInitial)
      setTouchStart(null)
      setIsZoomed(false)
    }
  }, [isOpen, initialIndex, validImages.length])

  // Prevent body scroll when open
  useEffect(() => {
    if (!isOpen) return

    // Save original overflow style
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  // Early return if not open or no valid images
  if (!isOpen) return null
  if (validImages.length === 0) return null

  // Ensure currentIndex is within bounds
  const safeIndex = Math.min(Math.max(0, currentIndex), validImages.length - 1)
  const currentImage = validImages[safeIndex]

  // Extra safety check
  if (!currentImage || !currentImage.src) return null

  // Handle swipe gestures for navigation (only when not zoomed)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isZoomed) {
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY })
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null || isZoomed) return

    const touchEnd = e.changedTouches[0].clientX
    const diff = touchStart.x - touchEnd

    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext()
      else goPrev()
    }

    setTouchStart(null)
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-lightbox-open
      onClick={handleClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top right controls: Zoom and Close */}
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); isZoomed ? handleZoomOut() : handleZoomIn() }}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label={isZoomed ? "Zoom out" : "Zoom in"}
        >
          <FontAwesomeIcon icon={isZoomed ? faMagnifyingGlassMinus : faMagnifyingGlassPlus} className="w-5 h-5" />
        </button>
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
        </button>
      </div>

      {/* Top left: EXIF info button with hover panel */}
      {hasExifData(currentImage.exif) && (
        <div className="absolute top-4 left-4 z-10 group" onClick={(e) => e.stopPropagation()}>
          <button
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Photo information"
          >
            <FontAwesomeIcon icon={faCircleInfo} className="w-5 h-5" />
          </button>
          <div className="absolute top-12 left-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto">
            <ExifPanel exif={currentImage.exif!} />
          </div>
        </div>
      )}

      {/* Previous button */}
      {hasMultiple && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev() }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="Previous image"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-5 h-5" />
        </button>
      )}

      {/* Image with zoom/pan/pinch support */}
      <div
        className={`w-full h-full flex items-center justify-center transition-opacity duration-50 ease-out ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <Suspense fallback={
          <img
            src={currentImage.src}
            alt={currentImage.alt || ""}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />
        }>
          <TransformWrapper
            key={`${safeIndex}-${currentImage.src}`}
            ref={transformRef}
            initialScale={1}
            minScale={1}
            maxScale={4}
            centerOnInit={true}
            doubleClick={{ mode: "toggle", step: 2 }}
            panning={{ disabled: !isZoomed }}
            onTransformed={handleTransform}
            smooth={true}
            wheel={{ smoothStep: 0.1 }}
          >
            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100%",
              }}
              contentStyle={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={currentImage.src}
                alt={currentImage.alt || ""}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </Suspense>
      </div>

      {/* Next button */}
      {hasMultiple && !isZoomed && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext() }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="Next image"
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-5 h-5" />
        </button>
      )}

      {/* Dots indicator */}
      {hasMultiple && !isZoomed && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {validImages.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx) }}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === safeIndex ? "bg-white" : "bg-white/40"
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
